import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdminPayments, AdminPayment } from 'services/admin/adminUsersService';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { AlertTriangle, Search, CheckCircle, XCircle, Clock, RefreshCcw, ArrowUp, ArrowDown, X, ChevronLeft, ChevronRight } from 'lucide-react';

export function PaymentsTable() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState<string>('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [sortBy, setSortBy] = useState<'payment_date' | 'amount' | 'created_on'>('payment_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Theme tokens
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#314158' : '#E2E8F0';
  const tableRowHoverBg = isDark ? '#162030' : '#F8FAFC';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const textSecondary = isDark ? '#90A1B9' : '#64748B';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#374151';
  const errorBg = isDark ? '#3B1010' : '#FEF2F2';
  const errorText = isDark ? '#F87171' : '#DC2626';
  const errorBorder = isDark ? '#7F1D1D' : '#EF4444';
  const paginationBorder = isDark ? '#314158' : '#E2E8F0';
  const paginationText = isDark ? '#90A1B9' : '#374151';
  const mutedIcon = isDark ? '#5A6E83' : '#9CA3AF';

  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    color: inputText,
  };

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const response = await getAdminPayments({
          page: currentPage,
          limit: itemsPerPage,
          search: searchQuery || undefined,
          status: statusFilter || undefined,
          subscriptionType: subscriptionTypeFilter || undefined,
          paymentMethod: paymentMethodFilter || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          minAmount: minAmount ? parseFloat(minAmount) : undefined,
          maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
          sortBy: sortBy,
          sortOrder: sortOrder
        });
        setPayments(response.payments || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.total || 0);
        setError(null);
      } catch (err: any) {
        setError(err.message || t('admin.errors.failedToFetchPayments', 'Failed to fetch payments'));
        setPayments([]);
        console.error('Error fetching payments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, subscriptionTypeFilter, paymentMethodFilter, startDate, endDate, minAmount, maxAmount, sortBy, sortOrder, t]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return { background: isDark ? '#022C22' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D', borderColor: isDark ? '#065F46' : '#BBF7D0' };
      case 'active':
        return { background: isDark ? '#162456' : '#DBEAFE', color: isDark ? '#8EC5FF' : '#1D4ED8', borderColor: isDark ? '#1C398E' : '#93C5FD' };
      case 'pending':
        return { background: isDark ? '#3B2F08' : '#FEF9C3', color: isDark ? '#FACC15' : '#A16207', borderColor: isDark ? '#713F12' : '#FDE68A' };
      case 'failed':
        return { background: isDark ? '#3B1010' : '#FEE2E2', color: isDark ? '#F87171' : '#DC2626', borderColor: isDark ? '#7F1D1D' : '#FECACA' };
      case 'cancelled':
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569', borderColor: isDark ? '#314158' : '#E2E8F0' };
      default:
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569', borderColor: isDark ? '#314158' : '#E2E8F0' };
    }
  };

  const getSubscriptionTypeStyle = (type: string): React.CSSProperties => {
    switch (type) {
      case 'Trial':
        return { background: isDark ? '#2D1B4E' : '#F3E8FF', color: isDark ? '#C084FC' : '#7C3AED', borderColor: isDark ? '#581C87' : '#DDD6FE' };
      case 'Starter':
        return { background: isDark ? '#162456' : '#DBEAFE', color: isDark ? '#8EC5FF' : '#1D4ED8', borderColor: isDark ? '#1C398E' : '#93C5FD' };
      default:
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569', borderColor: isDark ? '#314158' : '#E2E8F0' };
    }
  };

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleSubscriptionTypeChange = (value: string) => {
    setSubscriptionTypeFilter(value);
    setCurrentPage(1);
  };

  const handlePaymentMethodChange = (value: string) => {
    setPaymentMethodFilter(value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleMinAmountChange = (value: string) => {
    setMinAmount(value);
    setCurrentPage(1);
  };

  const handleMaxAmountChange = (value: string) => {
    setMaxAmount(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setSubscriptionTypeFilter('');
    setPaymentMethodFilter('');
    setStartDate('');
    setEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setSortBy('payment_date');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <button
          key="first"
          onClick={() => handlePageChange(1)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
        >
          1
        </button>
      );

      if (startPage > 2) {
        items.push(
          <span key="ellipsis-start" className="px-2" style={{ color: mutedIcon }}>...</span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={i === currentPage
            ? { background: '#007bff', color: '#FFFFFF', border: '1px solid #007bff' }
            : { border: `1px solid ${paginationBorder}`, color: paginationText }
          }
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="ellipsis-end" className="px-2" style={{ color: mutedIcon }}>...</span>
        );
      }

      items.push(
        <button
          key="last"
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
        >
          {totalPages}
        </button>
      );
    }

    return items;
  };

  if (error) {
    return (
      <div className="p-4 rounded" style={{ background: errorBg, borderLeft: `4px solid ${errorBorder}` }}>
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} style={{ color: errorText, marginTop: 2 }} />
          <p className="text-xs" style={{ color: errorText }}>{error}</p>
        </div>
      </div>
    );
  }

  if (loading && payments.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="medium" text={t('admin.loading.payments', 'Loading payments...')} />
      </div>
    );
  }

  const hasActiveFilters = searchQuery || statusFilter || subscriptionTypeFilter || paymentMethodFilter || startDate || endDate || minAmount || maxAmount;

  return (
    <div className="p-5 space-y-4">
      {/* Search and Filters */}
      <div className="space-y-3">
        {/* Row 1: Search and Primary Filters */}
        <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
          {/* Search Input */}
          <div className="lg:flex-[2] min-w-0">
            <div className="relative">
              <input
                type="text"
                placeholder={t('admin.payments.searchPlaceholder', 'Search by email, name, invoice#, transaction ID...')}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputStyle}
              />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: mutedIcon }} />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="lg:w-36 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
          >
            <option value="">{t('admin.payments.allStatuses')}</option>
            <option value="completed">{t('admin.payments.completed')}</option>
            <option value="active">{t('admin.payments.active')}</option>
            <option value="failed">{t('admin.payments.failed')}</option>
            <option value="pending">{t('admin.payments.pending')}</option>
            <option value="cancelled">{t('admin.payments.cancelled')}</option>
          </select>

          {/* Subscription Type Filter */}
          <select
            value={subscriptionTypeFilter}
            onChange={(e) => handleSubscriptionTypeChange(e.target.value)}
            className="lg:w-36 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
          >
            <option value="">{t('admin.payments.allSubscriptions')}</option>
            <option value="Trial">{t('admin.payments.trial')}</option>
            <option value="Starter">{t('admin.payments.starter')}</option>
          </select>

          {/* Payment Method Filter */}
          <select
            value={paymentMethodFilter}
            onChange={(e) => handlePaymentMethodChange(e.target.value)}
            className="lg:w-32 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
          >
            <option value="">{t('admin.payments.allMethods')}</option>
            <option value="card">{t('admin.payments.card')}</option>
            <option value="trial">{t('admin.payments.trial')}</option>
          </select>

          {/* Sort By */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="lg:w-36 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
          >
            <option value="payment_date">{t('admin.payments.paymentDateSort')}</option>
            <option value="amount">{t('admin.payments.amountSort')}</option>
            <option value="created_on">{t('admin.payments.createdDateSort')}</option>
          </select>

          {/* Sort Order */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="lg:w-20 px-2 py-2 text-xs rounded-md transition-colors flex items-center gap-1 whitespace-nowrap justify-center"
            style={{ border: `1px solid ${inputBorder}`, color: inputText }}
            title={sortOrder === 'asc' ? t('admin.payments.ascending') : t('admin.payments.descending')}
          >
            {sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
            {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
          </button>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="lg:w-24 px-2 py-2 text-xs rounded-md transition-colors flex items-center gap-1 whitespace-nowrap justify-center"
              style={{ border: `1px solid ${isDark ? '#7F1D1D' : '#FCA5A5'}`, color: errorText }}
            >
              <XCircle size={12} />
              {t('admin.payments.clear')}
            </button>
          )}
        </div>

        {/* Row 2: Date and Amount Filters */}
        <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
          <div className="lg:flex-1">
            <input
              type="date"
              placeholder={t('admin.payments.startDate')}
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={inputStyle}
            />
          </div>
          <div className="lg:flex-1">
            <input
              type="date"
              placeholder={t('admin.payments.endDate')}
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={inputStyle}
            />
          </div>
          <div className="lg:flex-1">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: mutedIcon }}>$</span>
              <input
                type="number"
                placeholder={t('admin.payments.minAmount')}
                value={minAmount}
                onChange={(e) => handleMinAmountChange(e.target.value)}
                min="0"
                step="0.01"
                className="w-full pl-7 pr-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputStyle}
              />
            </div>
          </div>
          <div className="lg:flex-1">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: mutedIcon }}>$</span>
              <input
                type="number"
                placeholder={t('admin.payments.maxAmount')}
                value={maxAmount}
                onChange={(e) => handleMaxAmountChange(e.target.value)}
                min="0"
                step="0.01"
                className="w-full pl-7 pr-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Active Filters Info */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 items-center text-xs" style={{ color: subtitleColor }}>
            <span className="font-medium">{t('admin.payments.activeFilters', 'Active filters')}:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#162456' : '#DBEAFE', color: isDark ? '#8EC5FF' : '#1D4ED8' }}>
                Search: "{searchQuery}"
                <button onClick={() => setSearchQuery('')}><X size={10} /></button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#022C22' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D' }}>
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('')}><X size={10} /></button>
              </span>
            )}
            {subscriptionTypeFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#2D1B4E' : '#F3E8FF', color: isDark ? '#C084FC' : '#7C3AED' }}>
                Type: {subscriptionTypeFilter}
                <button onClick={() => setSubscriptionTypeFilter('')}><X size={10} /></button>
              </span>
            )}
            {paymentMethodFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#1C2B5E' : '#E0E7FF', color: isDark ? '#A5B4FC' : '#4338CA' }}>
                Method: {paymentMethodFilter}
                <button onClick={() => setPaymentMethodFilter('')}><X size={10} /></button>
              </span>
            )}
            {(startDate || endDate) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#3B2F08' : '#FEF9C3', color: isDark ? '#FACC15' : '#A16207' }}>
                Date Range: {startDate || '...'} to {endDate || '...'}
                <button onClick={() => { setStartDate(''); setEndDate(''); }}><X size={10} /></button>
              </span>
            )}
            {(minAmount || maxAmount) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#431407' : '#FFF7ED', color: isDark ? '#FB923C' : '#EA580C' }}>
                Amount: ${minAmount || '0'} - ${maxAmount || '∞'}
                <button onClick={() => { setMinAmount(''); setMaxAmount(''); }}><X size={10} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Table Controls */}
      <div className="flex justify-between items-center">
        <div className="text-xs" style={{ color: subtitleColor }}>
          {t('admin.payments.showing', {
            from: payments.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0,
            to: Math.min(currentPage * itemsPerPage, totalItems),
            total: totalItems
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: subtitleColor }}>{t('admin.payments.rowsPerPage', 'Rows per page')}:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            disabled={loading}
            className="px-2 py-1 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 disabled:opacity-50"
            style={inputStyle}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${tableBorderColor}` }}>
        <table className="w-full">
          <thead style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorderColor}` }}>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.invoiceNumber', 'Invoice')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.user', 'User')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.amount', 'Amount')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.subscriptionType', 'Subscription')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.paymentMethod', 'Method')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.paymentDate', 'Date')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.status', 'Status')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.payments.subscriptionPeriod', 'Period')}
              </th>
            </tr>
          </thead>
          <tbody style={{ background: 'transparent' }}>
            {payments && payments.length > 0 ? payments.map((payment) => (
              <tr
                key={payment._id}
                onMouseEnter={() => setHoveredRow(payment._id)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{
                  background: hoveredRow === payment._id ? tableRowHoverBg : 'transparent',
                  borderBottom: `1px solid ${tableBorderColor}`,
                  transition: 'background-color 0.15s'
                }}
              >
                <td className="px-4 py-3">
                  <div className="text-xs font-medium" style={{ color: titleColor }}>{payment.invoiceNumber}</div>
                  {payment.transactionId && payment.transactionId !== 'trial' && (
                    <div className="text-xs font-mono" style={{ color: textSecondary }}>{payment.transactionId.substring(0, 12)}...</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs font-medium" style={{ color: titleColor }}>{payment.user?.name || 'N/A'}</div>
                  <div className="text-xs" style={{ color: textSecondary }}>{payment.user?.email || 'N/A'}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs font-semibold" style={{ color: titleColor }}>{formatCurrency(payment.amount || 0)}</div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                    style={{ ...getSubscriptionTypeStyle(payment.subscriptionType), border: `1px solid ${getSubscriptionTypeStyle(payment.subscriptionType).borderColor}` }}
                  >
                    {payment.subscriptionType}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs capitalize" style={{ color: titleColor }}>{payment.paymentMethod}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs" style={{ color: subtitleColor }}>{formatDate(payment.paymentDate)}</div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                    style={{ ...getStatusStyle(payment.status), border: `1px solid ${getStatusStyle(payment.status).borderColor}` }}
                  >
                    {payment.status === 'completed' && <CheckCircle size={12} style={{ marginRight: 4 }} />}
                    {payment.status === 'failed' && <XCircle size={12} style={{ marginRight: 4 }} />}
                    {payment.status === 'pending' && <Clock size={12} style={{ marginRight: 4 }} />}
                    {payment.status === 'active' && <RefreshCcw size={12} style={{ marginRight: 4 }} />}
                    {payment.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs" style={{ color: subtitleColor }}>
                    {formatDate(payment.subscriptionStartDate)}
                  </div>
                  <div className="text-xs" style={{ color: textSecondary }}>
                    to {formatDate(payment.subscriptionEndDate)}
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm" style={{ color: textSecondary }}>
                  {hasActiveFilters
                    ? t('admin.payments.noResultsFound', 'No payments found matching your filters')
                    : t('admin.payments.noPayments', 'No payments found')
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
          >
            <ChevronLeft size={12} />
            {t('admin.pagination.previous', 'Previous')}
          </button>

          <div className="flex items-center gap-1">
            {renderPaginationItems()}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
          >
            {t('admin.pagination.next', 'Next')}
            <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
